/**
 * Active tenant context store (Zustand).
 *
 * For platform admins, this allows switching between tenants.
 * For customer users, the tenant is automatically set from their profile.
 */

import { create } from 'zustand';
import type { Tenant } from '@/types/tenant';
import * as tenantApi from '@/api/tenants';

interface TenantState {
  /** Currently selected tenant (for platform admins viewing a specific org). */
  activeTenant: Tenant | null;
  /** All available tenants (populated for platform admins). */
  tenants: Tenant[];
  /** True while tenant data is loading. */
  isLoading: boolean;

  /** Fetch all tenants (platform admin only). */
  loadTenants: () => Promise<void>;
  /** Set the active tenant context. */
  setActiveTenant: (tenant: Tenant | null) => void;
  /** Clear tenant state (on logout). */
  reset: () => void;
}

export const useTenantStore = create<TenantState>((set) => ({
  activeTenant: null,
  tenants: [],
  isLoading: false,

  loadTenants: async () => {
    set({ isLoading: true });
    try {
      const tenants = await tenantApi.getTenants();
      set({ tenants, isLoading: false });
    } catch {
      set({ tenants: [], isLoading: false });
    }
  },

  setActiveTenant: (tenant) => set({ activeTenant: tenant }),

  reset: () => set({ activeTenant: null, tenants: [], isLoading: false }),
}));
